/**
 * AddTransactionModal Component
 * Modal form for manual transaction entry and editing
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Alert,
  Platform,
  Switch,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import { format } from 'date-fns';
import { useBudgetStore } from '../../store/budgetStore';
import type { Transaction } from '../../api/types';
import TagInput from './TagInput';
import logger from '../../utils/logger';

interface AddTransactionModalProps {
  visible: boolean;
  onClose: () => void;
  transaction?: Transaction | null;
}

const CATEGORY_OPTIONS = [
  { key: 'housing', label: 'Logement' },
  { key: 'food', label: 'Alimentation' },
  { key: 'transport', label: 'Transport' },
  { key: 'insurance', label: 'Assurance' },
  { key: 'subscriptions', label: 'Abonnements' },
  { key: 'leisure', label: 'Loisirs' },
  { key: 'health', label: 'Santé' },
  { key: 'taxes', label: 'Impôts' },
  { key: 'education', label: 'Éducation' },
  { key: 'family_assistance', label: 'Aide familiale' },
  { key: 'children_expenses', label: 'Enfants' },
  { key: 'debt_repayment', label: 'Crédit / Prêt' },
  { key: 'shopping', label: 'Shopping' },
  { key: 'savings', label: 'Épargne' },
  { key: 'bank_fees', label: 'Frais bancaires' },
  { key: 'salary', label: 'Salaire' },
  { key: 'freelance_income', label: 'Revenus business' },
  { key: 'investment_income', label: 'Revenus investissement' },
  { key: 'rental_income', label: 'Revenus locatifs' },
  { key: 'other_income', label: 'Autres revenus' },
  { key: 'other', label: 'Autre' },
];

export default function AddTransactionModal({ visible, onClose, transaction }: AddTransactionModalProps) {
  const { addTransaction, updateTransaction, deleteTransaction } = useBudgetStore();
  const isEditMode = !!transaction;

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [transactionType, setTransactionType] = useState<'expense' | 'income'>('expense');
  const [transactionDate, setTransactionDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [excludedFromBudget, setExcludedFromBudget] = useState(false);
  const [tags, setTags] = useState<string[]>([]);

  // Pre-fill form in edit mode
  useEffect(() => {
    if (transaction && visible) {
      setDescription(transaction.description);
      setAmount(String(Math.abs(transaction.amount)));
      setCategory(transaction.category);
      setTransactionType(transaction.transaction_type as 'expense' | 'income');
      setTransactionDate(transaction.transaction_date);
      setNotes(transaction.notes || '');
      setExcludedFromBudget(transaction.excluded_from_budget || false);
      setTags(transaction.tags || []);
    }
  }, [transaction, visible]);

  const resetForm = () => {
    setDescription('');
    setAmount('');
    setCategory('');
    setTransactionType('expense');
    setTransactionDate(format(new Date(), 'yyyy-MM-dd'));
    setNotes('');
    setExcludedFromBudget(false);
    setTags([]);
    setShowCategoryPicker(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      Alert.alert('Erreur', 'Veuillez entrer une description');
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      Alert.alert('Erreur', 'Veuillez entrer un montant valide');
      return;
    }

    if (!category) {
      Alert.alert('Erreur', 'Veuillez sélectionner une catégorie');
      return;
    }

    setIsSubmitting(true);
    try {
      const data = {
        description: description.trim(),
        amount: parsedAmount,
        category,
        transaction_type: transactionType,
        transaction_date: transactionDate,
        notes: notes.trim() || undefined,
        excluded_from_budget: excludedFromBudget || undefined,
        tags: tags.length > 0 ? tags : undefined,
      };

      if (isEditMode && transaction) {
        await updateTransaction(transaction.id, data);
      } else {
        await addTransaction(data);
      }
      handleClose();
    } catch (err: any) {
      logger.error(`[AddTransaction] ${isEditMode ? 'Update' : 'Create'} Error:`, err);
      const message = err.response?.data?.error ||
        (isEditMode ? 'Erreur lors de la mise à jour' : 'Erreur lors de l\'ajout');
      Alert.alert('Erreur', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!transaction) return;
    setIsDeleting(true);
    try {
      await deleteTransaction(transaction.id);
      handleClose();
    } catch {
      if (Platform.OS === 'web') {
        window.alert('Impossible de supprimer');
      } else {
        Alert.alert('Erreur', 'Impossible de supprimer');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = () => {
    if (!transaction) return;
    if (Platform.OS === 'web') {
      if (window.confirm('Supprimer cette transaction ?')) {
        doDelete();
      }
    } else {
      Alert.alert(
        'Supprimer',
        'Supprimer cette transaction ?',
        [
          { text: 'Annuler', style: 'cancel' },
          { text: 'Supprimer', style: 'destructive', onPress: doDelete },
        ]
      );
    }
  };

  const selectedCategoryLabel = CATEGORY_OPTIONS.find(c => c.key === category)?.label || '';

  const filteredCategories = transactionType === 'income'
    ? CATEGORY_OPTIONS.filter(c => ['salary', 'freelance_income', 'investment_income', 'rental_income', 'other_income', 'other'].includes(c.key))
    : CATEGORY_OPTIONS.filter(c => !['salary', 'freelance_income', 'investment_income', 'rental_income', 'other_income'].includes(c.key));

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.content}>
          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Header */}
            <View style={styles.header}>
              <Text style={styles.title}>
                {isEditMode ? 'Modifier la transaction' : 'Nouvelle transaction'}
              </Text>
              <TouchableOpacity onPress={handleClose}>
                <Icon name="close" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            {/* Type toggle */}
            <View style={styles.typeToggle}>
              <TouchableOpacity
                style={[
                  styles.typeButton,
                  transactionType === 'expense' && styles.typeButtonExpenseActive,
                ]}
                onPress={() => {
                  setTransactionType('expense');
                  if (!isEditMode) setCategory('');
                }}
              >
                <Icon
                  name="arrow-down"
                  size={18}
                  color={transactionType === 'expense' ? '#FFFFFF' : colors.error}
                />
                <Text style={[
                  styles.typeButtonText,
                  transactionType === 'expense' && styles.typeButtonTextActive,
                ]}>
                  Dépense
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.typeButton,
                  transactionType === 'income' && styles.typeButtonIncomeActive,
                ]}
                onPress={() => {
                  setTransactionType('income');
                  if (!isEditMode) setCategory('');
                }}
              >
                <Icon
                  name="arrow-up"
                  size={18}
                  color={transactionType === 'income' ? '#FFFFFF' : colors.success}
                />
                <Text style={[
                  styles.typeButtonText,
                  transactionType === 'income' && styles.typeButtonTextActive,
                ]}>
                  Revenu
                </Text>
              </TouchableOpacity>
            </View>

            {/* Description */}
            <Text style={styles.label}>Description</Text>
            <TextInput
              style={styles.input}
              value={description}
              onChangeText={setDescription}
              placeholder="Ex: Courses Carrefour"
              placeholderTextColor={colors.textSecondary}
            />

            {/* Amount */}
            <Text style={styles.label}>Montant (€)</Text>
            <TextInput
              style={styles.input}
              value={amount}
              onChangeText={setAmount}
              keyboardType="numeric"
              placeholder="Ex: 45.50"
              placeholderTextColor={colors.textSecondary}
            />

            {/* Category */}
            <Text style={styles.label}>Catégorie</Text>
            <TouchableOpacity
              style={styles.pickerButton}
              onPress={() => setShowCategoryPicker(!showCategoryPicker)}
            >
              <Text style={[
                styles.pickerButtonText,
                !category && styles.pickerButtonPlaceholder,
              ]}>
                {selectedCategoryLabel || 'Sélectionner une catégorie'}
              </Text>
              <Icon
                name={showCategoryPicker ? 'chevron-up' : 'chevron-down'}
                size={20}
                color={colors.textSecondary}
              />
            </TouchableOpacity>

            {showCategoryPicker && (
              <ScrollView style={styles.categoryList} nestedScrollEnabled={true}>
                {filteredCategories.map((cat) => (
                  <TouchableOpacity
                    key={cat.key}
                    style={[
                      styles.categoryItem,
                      category === cat.key && styles.categoryItemSelected,
                    ]}
                    onPress={() => {
                      setCategory(cat.key);
                      setShowCategoryPicker(false);
                    }}
                  >
                    <Text style={[
                      styles.categoryItemText,
                      category === cat.key && styles.categoryItemTextSelected,
                    ]}>
                      {cat.label}
                    </Text>
                    {category === cat.key && (
                      <Icon name="check" size={18} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}

            {/* Date */}
            <Text style={styles.label}>Date</Text>
            <TextInput
              style={styles.input}
              value={transactionDate}
              onChangeText={setTransactionDate}
              placeholder="AAAA-MM-JJ"
              placeholderTextColor={colors.textSecondary}
            />

            {/* Notes */}
            <Text style={styles.label}>Notes (optionnel)</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={notes}
              onChangeText={setNotes}
              placeholder="Ajouter une note..."
              placeholderTextColor={colors.textSecondary}
              multiline
              numberOfLines={3}
            />

            {/* Excluded from budget toggle */}
            <View style={styles.toggleRow}>
              <View style={styles.toggleInfo}>
                <Icon name="eye-off-outline" size={18} color={colors.textSecondary} />
                <Text style={styles.toggleLabel}>Exclure du budget</Text>
              </View>
              <Switch
                value={excludedFromBudget}
                onValueChange={setExcludedFromBudget}
                trackColor={{ false: colors.border, true: colors.primary + '80' }}
                thumbColor={excludedFromBudget ? colors.primary : '#f4f3f4'}
              />
            </View>

            {/* Tags */}
            <Text style={styles.label}>Tags (optionnel)</Text>
            <TagInput tags={tags} onChange={setTags} />

            {/* Delete button (edit mode only) */}
            {isEditMode && (
              <TouchableOpacity
                style={styles.deleteButton}
                onPress={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <ActivityIndicator size="small" color={colors.error} />
                ) : (
                  <>
                    <Icon name="delete-outline" size={18} color={colors.error} />
                    <Text style={styles.deleteButtonText}>Supprimer cette transaction</Text>
                  </>
                )}
              </TouchableOpacity>
            )}

            {/* Actions */}
            <View style={styles.actions}>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton]}
                onPress={handleClose}
              >
                <Text style={styles.cancelButtonText}>Annuler</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.button,
                  styles.submitButton,
                  isSubmitting && styles.submitButtonDisabled,
                ]}
                onPress={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>
                    {isEditMode ? 'Enregistrer' : 'Ajouter'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    backgroundColor: colors.background,
    borderRadius: 16,
    padding: spacing.lg,
    width: '90%',
    maxWidth: 440,
    maxHeight: '85%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
  },
  typeToggle: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  typeButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background,
  },
  typeButtonExpenseActive: {
    backgroundColor: colors.error,
    borderColor: colors.error,
  },
  typeButtonIncomeActive: {
    backgroundColor: colors.success,
    borderColor: colors.success,
  },
  typeButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  typeButtonTextActive: {
    color: '#FFFFFF',
  },
  label: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  input: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    fontSize: typography.fontSize.md,
    color: colors.text,
    marginBottom: spacing.md,
  },
  textArea: {
    minHeight: 70,
    textAlignVertical: 'top',
  },
  pickerButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.md,
  },
  pickerButtonText: {
    fontSize: typography.fontSize.md,
    color: colors.text,
  },
  pickerButtonPlaceholder: {
    color: colors.textSecondary,
  },
  categoryList: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: spacing.md,
    maxHeight: 200,
  },
  categoryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  categoryItemSelected: {
    backgroundColor: colors.primary + '10',
  },
  categoryItemText: {
    fontSize: typography.fontSize.md,
    color: colors.text,
  },
  categoryItemTextSelected: {
    color: colors.primary,
    fontWeight: typography.fontWeight.semibold as '600',
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    marginTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  deleteButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
  },
  toggleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },
  toggleInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  toggleLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  button: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
  },
  cancelButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  submitButton: {
    backgroundColor: colors.primary,
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: '#FFFFFF',
  },
});
